"use client";

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils/cn";
import { LockedItemTooltip, SidebarSection, SidebarTooltip } from "@/components/layout/sidebar-primitives";
import { useFeatureValue } from "@growthbook/growthbook-react";
import { useBookingCta } from "@/features/booking/use-booking-cta";

interface SidebarNavigationProps {
  collapsed: boolean;
  sections: SidebarSection[];
  onNavigate: () => void;
}

export function SidebarNavigation({
  collapsed,
  sections,
  onNavigate,
}: SidebarNavigationProps) {
  const { t } = useTranslation();
  const bookingTouchpointsOn = useFeatureValue("booking-touchpoints", false);
  const bookingCta = useBookingCta("locked_feature");
  return (
    <div className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden py-1">
      {sections.map((section, sectionIndex) => (
        <div key={section.id}>
          {sectionIndex > 0 && (
            <div className={cn("h-px bg-border my-2", collapsed ? "mx-3" : "mx-4")} />
          )}
          {section.label && !collapsed && (
            <p className="mb-1.5 px-5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {section.label}
            </p>
          )}
          <nav className={cn("space-y-px", collapsed ? "px-2" : "px-3")}>
            {section.items.map((item) => {
              const isExternal = item.href?.startsWith("http");
              const itemClassName = cn(
                "group relative flex w-full items-center rounded-lg py-2 text-[13px] font-medium text-left transition-all duration-150",
                collapsed ? "justify-center" : "px-3",
                item.active
                  ? "bg-muted text-primary dark:bg-primary/10 dark:text-muted-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
                item.disabled && "pointer-events-none opacity-50",
              );

              const itemContent = (
                <>
                  {item.active && (
                    <div className="absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-full bg-gradient-to-b from-gradient-from to-gradient-to" />
                  )}
                  {item.icon && (
                    <span
                      className={cn(
                        "flex h-[17px] w-[17px] shrink-0 items-center justify-center",
                        !collapsed && "mr-2.5",
                        item.active ? "text-primary" : "text-muted-foreground",
                      )}
                    >
                      {item.icon}
                    </span>
                  )}
                  {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
                  {!collapsed && item.badge && <span className="ml-1 shrink-0">{item.badge}</span>}
                </>
              );

              const navElement = (
                item.href ? (
                  isExternal ? (
                    <a
                      key={item.id}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={item.ariaLabel}
                      onClick={() => {
                        item.onClick?.();
                        onNavigate();
                      }}
                      className={itemClassName}
                    >
                      {itemContent}
                    </a>
                  ) : (
                    <Link
                      key={item.id}
                      to={item.href}
                      aria-label={item.ariaLabel}
                      aria-current={item.active ? "page" : undefined}
                      onClick={() => {
                        item.onClick?.();
                        onNavigate();
                      }}
                      className={itemClassName}
                    >
                      {itemContent}
                    </Link>
                  )
                ) : (
                  <button
                    key={item.id}
                    type="button"
                    aria-label={item.ariaLabel}
                    onClick={() => {
                      item.onClick?.();
                      onNavigate();
                    }}
                    className={itemClassName}
                  >
                    {itemContent}
                  </button>
                )
              );

              if (item.disabled) {
                return (
                  <LockedItemTooltip
                    key={item.id}
                    title={t("MENU.LOCKED_TOOLTIP_TITLE")}
                    description={t("MENU.LOCKED_TOOLTIP_DESC")}
                    ctaLabel={t("MENU.LOCKED_TOOLTIP_CTA")}
                    ctaHref="/upload"
                    onCtaClick={onNavigate}
                  >
                    {navElement}
                  </LockedItemTooltip>
                );
              }

              if (item.lockedUpsell) {
                return (
                  <LockedItemTooltip
                    key={item.id}
                    title={item.lockedUpsell.title}
                    description={item.lockedUpsell.description}
                    ctaLabel={
                      bookingTouchpointsOn && bookingCta.qualified
                        ? t(bookingCta.label.key, bookingCta.label.text)
                        : t("MENU.TALK_TO_US", "Talk to us")
                    }
                    onCtaClick={
                      bookingTouchpointsOn && bookingCta.qualified
                        ? () => {
                            bookingCta.trackClick();
                            window.open(bookingCta.href, "_blank", "noopener,noreferrer");
                          }
                        : item.onClick
                    }
                  >
                    {navElement}
                  </LockedItemTooltip>
                );
              }

              return (
                <SidebarTooltip key={item.id} label={item.label} show={collapsed}>
                  {navElement}
                </SidebarTooltip>
              );
            })}
          </nav>
        </div>
      ))}
    </div>
  );
}
